import React from 'react'
import PropTypes from 'prop-types'
import numeral from 'numeral'

import Dropdown from 'Pods/Shared/Dropdown'
import CustomCheckbox from 'Pods/Shared/Checkbox/CustomCheckbox'

import CollectionsButton from './CollectionsButton'
import BatchDraftEditorButton from './BatchDraftEditorButton'
import CustomLicenseModal from './CustomLicenseModal'

const priceLimits = {
  min: 2,
  printableMin: 3,
  max: 10000,
  transformMin: 1,
  transformMax: 99,
}

class BulkActions extends React.Component {
  constructor() {
    super()

    this.state = {
      active: false,
      action: 'price_set',
      price: null,
      error: null,
      inputErrorClass: '',
      selectedLicense: null,
      showCustomLicenseModal: false,
    }

    this.printableModelsSelected = this.printableModelsSelected.bind(this)
    this.onPriceSubmit = this.onPriceSubmit.bind(this)
    this.handleLicenseSubmit = this.handleLicenseSubmit.bind(this)
    this.onLicenseSubmit = this.onLicenseSubmit.bind(this)
    this.onItemUpdateCompleted = this.onItemUpdateCompleted.bind(this)
  }

  printableModelsSelected() {
    return this.props.selectedItems.filter(model => model.attributes.printReady).length > 0
  }

  onActionChange(e) {
    const inputErrorClass = this.inputClass(e.target.value, this.state.price)

    this.setState({
      action: e.target.value, error: null, inputErrorClass, active: inputErrorClass === '',
    })

    this.showError(e.target.value, this.state.price)
  }

  onLicenseChange(e) {
    this.setState({
      selectedLicense: e.target.value,
      active: true,
    })
  }

  onPriceInput(e) {
    const inputErrorClass = this.inputClass(this.state.action, numeral(e.target.value).value())

    this.setState({
      price: e.target.value, error: null, inputErrorClass, active: inputErrorClass === '',
    })

    this.showError(this.state.action, numeral(e.target.value).value())
  }

  inputClass(action, price) {
    const minPrice = this.printableModelsSelected() ? priceLimits.printableMin : priceLimits.min

    if (action === 'price_set' && (price < minPrice || price > priceLimits.max)) {
      return 'is-invalid'
    } if (action !== 'price_set' && (price < priceLimits.transformMin || price > priceLimits.transformMax)) {
      return 'is-invalid'
    }
    return ''
  }

  fieldValidationMessage(message) {
    return I18n.t(`profile.my_products.errors.${message}`)
  }

  showError(action, price, limitsReached) {
    const minPrice = this.printableModelsSelected() ? priceLimits.printableMin : priceLimits.min
    let message = null

    if (action === 'price_set') {
      if (!price) {
        message = 'empty'
      } else if (price < minPrice) {
        message = this.printableModelsSelected() ? 'min_3dp_fixed' : 'min_fixed'
      } else if (price > priceLimits.max) {
        message = 'max_fixed'
      }

      if (limitsReached) {
        message = 'max_reached'
      }
    } else if (action === 'price_increase' || action === 'price_decrease') {
      if (!price) {
        message = 'empty'
      } else if (price < priceLimits.transformMin) {
        message = 'min_percent'
      } else if (price > priceLimits.transformMax) {
        message = 'max_percent'
      }

      if (limitsReached) {
        message = action === 'price_increase' ? 'max_reached' : 'min_reached'
      }
    }

    this.setState({ error: message ? this.fieldValidationMessage(message) : message })
  }

  onPriceSubmit() {
    const inputErrorClass = this.inputClass(this.state.action, this.state.price)

    this.setState({ active: false, error: null, inputErrorClass })

    const payload = { data: [] }

    if (inputErrorClass !== 'is-invalid') {
      this.props.selectedItems.forEach((item) => {
        const newPrice = this.calculateNewPrice(item.attributes.price, this.state.action, this.state.price)

        if (newPrice !== item.attributes.price) {
          item.attributes.price = newPrice

          payload.data.push({ id: item.id, type: 'items', attributes: { price: newPrice } })
        }
      })

      if (payload.data && payload.data.length > 0) {
        axios.patch('/api/internal/items', payload)
          .then(this.onItemUpdateCompleted)
          .catch((xhr) => { App.events.publish('app:notification:flash:error', xhr.response.data.errors[0].detail) })
      } else {
        this.showError(this.state.action, this.state.price, true)
      }
    }
  }

  handleLicenseSubmit() {
    const newLicense = this.state.selectedLicense

    if (['custom_no_ai', 'custom'].includes(newLicense)) {
      this.setState({ showCustomLicenseModal: true })
    } else {
      this.onLicenseSubmit()
    }
  }

  onLicenseSubmit(customLicense = null) {
    this.setState({ active: false })

    const payload = { data: [] }
    const newLicense = this.state.selectedLicense

    this.props.selectedItems.forEach((item) => {
      if (newLicense !== item.attributes.license) {
        item.attributes.license = newLicense
        let attributes = { license: newLicense }
        if (customLicense) {
          attributes = { ...attributes, custom_license: customLicense }
        }

        payload.data.push({
          id: item.id,
          type: 'items',
          attributes,
        })
      }
    })

    if (payload.data.length > 0) {
      axios.patch('/api/internal/items', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(this.onItemUpdateCompleted)
        .catch((xhr) => {
          App.events.publish('app:notification:flash:error', xhr.response.data.errors[0].detail)
        })
    } else {
      this.showError(this.state.action, this.state.license, true)
    }
  }

  calculateNewPrice(oldPrice, action, value) {
    const minPrice = this.printableModelsSelected() ? priceLimits.printableMin : priceLimits.min
    let newPrice = value

    if (action === 'price_increase') {
      newPrice = oldPrice + (oldPrice * value / 100)
    } else if (action === 'price_decrease') {
      newPrice = oldPrice - (oldPrice * value / 100)
    }

    return Math.max(minPrice, Math.min(newPrice, priceLimits.max))
  }

  onItemUpdateCompleted() {
    this.setState({ active: true })
    this.props.onFiltersChange()
  }

  getLicenses() {
    const allLicenses = [
      { id: 'royalty_free', title: 'Royalty Free', value: 'royalty_free' },
      { id: 'royalty_free_no_ai', title: 'Royalty Free, no Ai', value: 'royalty_free_no_ai' },
      { id: 'custom', title: 'Custom', value: 'custom' },
      { id: 'custom_no_ai', title: 'Custom, no Ai', value: 'custom_no_ai' },
      { id: 'editorial', title: 'Editorial', value: 'editorial' },
      { id: 'editorial_no_ai', title: 'Editorial, no Ai', value: 'editorial_no_ai' },
    ]

    const freeLicenses = [
      { id: 'royalty_free_no_ai', title: 'Royalty Free, no Ai', value: 'royalty_free_no_ai' },
      { id: 'custom_no_ai', title: 'Custom, no Ai', value: 'custom_no_ai' },
      { id: 'editorial_no_ai', title: 'Editorial, no Ai', value: 'editorial_no_ai' },
    ]

    const { selectedItems } = this.props
    const hasFreeItem = selectedItems.some(item => item.attributes.price === 0)
    return hasFreeItem ? freeLicenses : allLicenses
  }

  render() {
    const alternatives = [
      { id: 'price-set', title: 'Set to', value: 'price_set' },
      { id: 'price-increase', title: 'Increase by', value: 'price_increase' },
      { id: 'price-decrease', title: 'Decrease by', value: 'price_decrease' },
    ]

    return (
      <div className="products-filtering__bulk-actions">
        <CustomCheckbox
          text={`${this.props.selectedItems.length} Selected:`}
          color="primary"
          customClass="btn-show-selected"
          isDisabled={!this.props.showOnlySelected && this.props.selectedItems.length === 0}
          onTrigger={this.props.onShowSelectedTrigger}
        />

        <BatchDraftEditorButton
          selectedItems={this.props.selectedItems}
          isDisabled={this.props.selectedItems.length < 2}
          clearSelection={this.props.clearSelection}
        />

        <Dropdown title="Set Price" titleIcon={['fas', 'dollar-sign']} isDisabled={this.props.selectedItems.length === 0 || this.props.isAddingToCollection}>
          {alternatives.map((alternative) => (
            <div className="u-mb10" key={alternative.id}>
              <input
                type="radio"
                id={alternative.id}
                name="price-action"
                className="radio"
                value={alternative.value}
                onChange={(e) => this.onActionChange(e)}
                checked={this.state.action === alternative.value}
              />
              <label htmlFor={alternative.id}>{alternative.title}</label>
            </div>
          ))}
          <div className="bulk-actions__input u-mb10">
            <div className="form-group form-group--with-prefix">
              {this.state.error && <div className="error-message--tooltip tooltip--left">{this.state.error}</div>}
              <div className="form-group-prepend">
                <span className={`form-group-text ${this.state.inputErrorClass}`}>{this.state.action === 'price_set' ? '$' : '%'}</span>
              </div>
              {
                this.state.action === 'price_set' ? (
                  <input
                    type="number"
                    min={this.printableModelsSelected() ? priceLimits.printableMin : priceLimits.min}
                    max={priceLimits.max}
                    className={`form-control ${this.state.inputErrorClass}`}
                    onChange={(e) => this.onPriceInput(e)}
                  />
                ) : (
                  <input
                    type="number"
                    min={priceLimits.transformMin}
                    max={priceLimits.transformMax}
                    className={`form-control ${this.state.inputErrorClass}`}
                    onChange={(e) => this.onPriceInput(e)}
                  />
                )
              }
            </div>
          </div>

          <button
            className="btn btn-primary btn-block"
            disabled={!this.state.active}
            onClick={this.onPriceSubmit}
          >
            Set price
          </button>
        </Dropdown>
        <Dropdown title="Set License" className="u-ml10" isDisabled={this.props.selectedItems.length === 0 || this.props.isAddingToCollection}>
          {this.getLicenses().map((license) => (
            <div className="bulk-actions__option u-mb10" key={license.id}>
              <input
                type="radio"
                id={license.id}
                name="license-action"
                className="radio"
                value={license.value}
                onChange={(e) => this.onLicenseChange(e)}
                checked={this.state.selectedLicense === license.value}
              />
              <label htmlFor={license.id}>{license.title}</label>
            </div>
          ))}
          <button
            className="btn btn-primary btn-block"
            onClick={this.handleLicenseSubmit}
            disabled={!this.state.active}
          >
            Set license
          </button>
        </Dropdown>

        {this.state.showCustomLicenseModal && (
          <CustomLicenseModal
            modalIsOpen={this.state.showCustomLicenseModal}
            license={this.state.selectedLicense}
            onSubmit={this.onLicenseSubmit}
            closeModal={() => this.setState({ showCustomLicenseModal: false })}
          />
        )}

        <CollectionsButton
          selectedItems={this.props.selectedItems}
          isDisabled={this.props.selectedItems.length < 2}
          editableCollectionId={this.props.editableCollectionId}
          editCollection={this.props.editCollection}
          isAddingToCollection={this.props.isAddingToCollection}
          triggerAddToCollection={this.props.triggerAddToCollection}
        />
      </div>
    )
  }
}

BulkActions.propTypes = {
  onFiltersChange: PropTypes.func.isRequired,
  selectedItems: PropTypes.array.isRequired,
  editableCollectionId: PropTypes.string.isRequired,
  editCollection: PropTypes.func.isRequired,
  onShowSelectedTrigger: PropTypes.func.isRequired,
  showOnlySelected: PropTypes.bool.isRequired,
  clearSelection: PropTypes.func.isRequired,
  isAddingToCollection: PropTypes.bool.isRequired,
  triggerAddToCollection: PropTypes.func.isRequired,
}

export default BulkActions
